<script lang="ts">
  import { untrack } from 'svelte';
  import { game } from './stores/game.svelte.js';
  import Board from './Board.svelte';
  import ModeratorPanel from './ModeratorPanel.svelte';
  import CaptureTally from './CaptureTally.svelte';
  import PromotionDialog from './PromotionDialog.svelte';
  import PhantomPalette from './PhantomPalette.svelte';
  import { pieceGlyph } from './pieces.js';
  import { phantoms } from './stores/phantoms.svelte.js';
  import { phantomDrag } from './stores/phantom-drag.svelte.js';
  import { isPromotionMove, type PromotionType, type Square } from '@blind-chess/shared';

  interface Props { gameId: string; }
  let { gameId }: Props = $props();

  let armedSquare: Square | null = $state(null);
  let pendingPromotion: { from: Square; to: Square } | null = $state(null);

  // Lifecycle effect: connect once when gameId becomes known, disconnect on
  // teardown. untrack() prevents the connect/disconnect call from registering
  // any state reads as effect dependencies — without it, reading state.ws
  // inside connect() triggers Svelte's effect_update_depth_exceeded loop.
  $effect(() => {
    const id = gameId;
    untrack(() => game.connect(id));
    return () => untrack(() => game.disconnect());
  });

  // Once the server commits, our local arm clears (visual state slaved to server).
  $effect(() => {
    if (game.state.touchedPiece) armedSquare = null;
    if (game.state.gameStatus === 'finished') armedSquare = null;
  });

  function onArm(sq: Square | null) {
    armedSquare = sq;
  }

  function onCommit(from: Square, to: Square) {
    const piece = game.state.view?.pieces[from];
    if (!piece) return;
    // A pawn promotes only from the rank adjacent to its promotion rank —
    // isPromotionMove checks the source rank, destination rank, and file delta,
    // so a pawn elsewhere "moved" toward the last rank no longer pops this
    // dialog (which a click on a phantom-occupied back-rank square could do).
    if (isPromotionMove(piece, from, to)) {
      pendingPromotion = { from, to };
      return;
    }
    game.commit(from, to);
  }

  function choosePromotion(p: PromotionType) {
    if (!pendingPromotion) return;
    game.commit(pendingPromotion.from, pendingPromotion.to, p);
    pendingPromotion = null;
  }

  const joinUrl = $derived(`${location.origin}/#/g/${gameId}`);

  let copied = $state(false);
  function copyLink() {
    navigator.clipboard.writeText(joinUrl);
    copied = true;
    setTimeout(() => copied = false, 1500);
  }

  const isBotTurn = $derived(
    !!game.state.aiOpponent
      && game.state.gameStatus === 'active'
      && game.state.view?.toMove === game.state.aiOpponent.color,
  );

  const aiBadgeText = $derived.by(() => {
    if (!game.state.aiOpponent) return null;
    return game.state.aiOpponent.brain === 'casual' ? 'Casual bot' : 'gemma4 recon';
  });

  const turnLabel = $derived.by(() => {
    if (game.state.gameStatus === 'finished') {
      const reason = game.state.endReason;
      const winner = game.state.winner;
      if (reason === 'checkmate') return `Checkmate — ${winner === 'w' ? 'White' : 'Black'} wins`;
      if (reason === 'resign') return `${winner === 'w' ? 'White' : 'Black'} wins by resignation`;
      if (reason === 'abandoned') return winner ? `${winner === 'w' ? 'White' : 'Black'} wins by abandonment` : 'Game abandoned';
      if (reason) return `Draw — ${reason}`;
      return 'Game over';
    }
    if (game.state.gameStatus === 'waiting') return 'Waiting for opponent…';
    if (!game.state.you) return '…';
    if (game.state.view?.toMove === game.state.you) return 'Your turn';
    if (game.state.aiOpponent) {
      const name = game.state.aiOpponent.brain === 'casual' ? 'Casual bot' : 'gemma4 recon';
      return `${name} is moving…`;
    }
    return 'Opponent thinking';
  });

  const oppColor = $derived<'w' | 'b'>(game.state.you === 'w' ? 'b' : 'w');

  // Phantom layer is blind-mode-only and shown only during active play.
  const phantomLayerEnabled = $derived(
    game.state.mode === 'blind' && game.state.gameStatus === 'active',
  );

  // The piece type currently being dragged (for the floating ghost), or null.
  const dragGhost = $derived.by(() => {
    const a = phantomDrag.state.active;
    return a && phantomDrag.state.moved ? a.type : null;
  });

  // Load the phantom layer when `you` is known (blind games only). Keyed on
  // gameId — like the connection effect — so it reloads if this <Game>
  // instance is reused for a different game without a remount.
  let loadedFor: string | null = $state(null);
  $effect(() => {
    const id = gameId;
    const you = game.state.you;
    if (loadedFor === id) return;
    if (you && game.state.mode === 'blind') {
      untrack(() => phantoms.loadForGame(id, you));
      loadedFor = id;
    }
  });

  // Drop the phantom layer when the game ends.
  $effect(() => {
    if (game.state.gameStatus === 'finished') {
      untrack(() => phantoms.clearForGame(gameId));
    }
  });
</script>

<div class="game-layout" class:waiting={game.state.gameStatus === 'waiting'}>

  <div class="topbar">
    <a href="#/" class="back">← New game</a>
    <span class="status">{turnLabel}</span>
    <span class="mode-badge">
      {game.state.mode ?? '…'}
      {#if game.state.you}
        · You: {game.state.you === 'w' ? 'White' : 'Black'}
      {/if}
    </span>
    {#if aiBadgeText}
      <span class="ai-badge" class:thinking={isBotTurn}>
        {aiBadgeText}
      </span>
    {/if}
  </div>

  {#if game.state.gameStatus === 'waiting'}
    <div class="waiting-card">
      <h2>Share this link to start the game</h2>
      <p class="muted">The first person to open it claims the open color.</p>
      <div class="link-row">
        <input class="link" type="text" readonly value={joinUrl} onclick={(e) => (e.currentTarget as HTMLInputElement).select()} />
        <button class="primary" onclick={copyLink}>{copied ? 'Copied!' : 'Copy'}</button>
      </div>
    </div>
  {:else if game.state.view && game.state.you}
    <div class="board-area">
      <Board
        pieces={game.state.view.pieces}
        you={game.state.you}
        toMove={game.state.view.toMove}
        mode={game.state.mode ?? 'blind'}
        highlightingEnabled={game.state.highlightingEnabled}
        phantoms={phantomLayerEnabled ? phantoms.state.phantoms : {}}
        phantomsEnabled={phantomLayerEnabled}
        armedSquare={armedSquare}
        touchedSquare={game.state.touchedPiece}
        {onArm}
        {onCommit}
      />
      {#if phantomLayerEnabled}
        <PhantomPalette {oppColor} />
      {/if}
    </div>

    <aside class="side">
      <ModeratorPanel announcements={game.state.announcements} you={game.state.you} />
      <CaptureTally captures={game.state.captures} you={game.state.you} />

      <div class="actions">
        {#if game.state.gameStatus === 'active'}
          <button onclick={() => game.offerDraw()} disabled={!!game.state.drawOffer}>Offer draw</button>
          <button onclick={() => { if (confirm('Resign?')) game.resign(); }}>Resign</button>
        {/if}
      </div>

      {#if game.state.drawOffer && game.state.drawOffer.from !== game.state.you}
        <div class="banner">
          Opponent offers a draw.
          <div class="row">
            <button class="primary" onclick={() => game.respondDraw(true)}>Accept</button>
            <button onclick={() => game.respondDraw(false)}>Decline</button>
          </div>
        </div>
      {:else if game.state.drawOffer && game.state.drawOffer.from === game.state.you}
        <div class="banner muted">Draw offer pending…</div>
      {/if}

      {#if game.state.lastError && Date.now() - game.state.lastError.at < 4000}
        <div class="banner err">⚠ {game.state.lastError.code}: {game.state.lastError.message}</div>
      {/if}

      {#if !game.state.opponentConnected && game.state.gameStatus === 'active' && !game.state.aiOpponent}
        <div class="banner muted">Opponent disconnected — 5 minute grace window.</div>
      {/if}
    </aside>
  {/if}
</div>

{#if phantomLayerEnabled && dragGhost}
  <div
    class="drag-ghost piece-{oppColor}"
    style="left: {phantomDrag.state.x}px; top: {phantomDrag.state.y}px;"
  >{pieceGlyph({ color: oppColor, type: dragGhost })}</div>
{/if}

{#if pendingPromotion && game.state.you}
  <PromotionDialog
    color={game.state.you}
    onChoose={choosePromotion}
    onCancel={() => pendingPromotion = null}
  />
{/if}

<style>
  .game-layout {
    display: grid;
    gap: 16px;
    padding: 12px;
    height: 100%;
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr;
  }

  @media (min-width: 900px) {
    .game-layout {
      grid-template-columns: 1fr 320px;
      grid-template-rows: auto 1fr;
      grid-template-areas:
        'top top'
        'board side';
      max-width: 1200px;
      margin: 0 auto;
    }
    .topbar { grid-area: top; }
    .board-area { grid-area: board; }
    .side { grid-area: side; }
  }

  .topbar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 8px;
  }
  .back { color: var(--text-dim); }
  .status { flex: 1; text-align: center; font-weight: 600; }
  .mode-badge {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-dim);
  }

  .board-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .side {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 280px;
  }
  .actions { display: flex; gap: 8px; }
  .actions button { flex: 1; }

  .banner {
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 14px;
  }
  .banner.err { border-color: #f87171; color: #f87171; }
  .banner .row { display: flex; gap: 8px; margin-top: 8px; }
  .banner .row button { flex: 1; }

  .ai-badge {
    font-size: 12px;
    font-weight: 600;
    padding: 4px 10px;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--panel);
    color: var(--text-dim);
    white-space: nowrap;
  }
  .ai-badge.thinking {
    color: var(--accent);
    border-color: var(--accent);
    background: rgba(211, 84, 0, 0.07);
  }

  .waiting-card {
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 24px;
    max-width: 520px;
    margin: 40px auto;
  }
  .waiting-card h2 { margin: 0 0 8px; font-size: 18px; }
  .link-row { display: flex; gap: 8px; margin-top: 16px; }
  .link { flex: 1; font-family: ui-monospace, monospace; font-size: 13px; }

  .drag-ghost {
    position: fixed;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 1000;
    font-size: 44px;
    line-height: 1;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
  }
  .drag-ghost.piece-w { color: #fafafa; }
  /* A dark dragged piece needs a light outline (the base dark shadow above
     does nothing for it) so it reads over dark areas of the page. */
  .drag-ghost.piece-b { color: #1a1a1a; text-shadow: 0 0 3px var(--text), 0 0 2px var(--text); }
</style>
